<template>
    <div v-loading="loading">
        <StorageStatusInformation :documnetsCount="documentCount" />

        <div>
            <div v-if="!uploadDisabled" class="tittle">
                {{ tittle }}
            </div>

            <div v-if="!uploadDisabled" class="file-type-chips">
                <el-badge v-for="chip in fileTypeChips"
                          :key="chip.color"
                          :value="$t('fileUpload.new')"
                          :hidden="!chip.isNew">
                    <el-tag :color="chip.color"
                            effect="dark"
                            class="file-type-chip">
                        {{ chip.ext }}
                    </el-tag>
                </el-badge>
            </div>

            <div class="file-loader">
                <el-upload id="file-upload"
                           ref="upload"
                           action="#"
                           :auto-upload="false"
                           :accept="acceptedTypes.join(',')"
                           :disabled="uploadDisabled"
                           :on-change="handleFileChange"
                           :on-remove="handleFileRemove"
                           :limit="1"
                           :file-list="fileList">
                    <div class="button-container">
                        <el-button type="primary"
                                   :disabled="uploadDisabled || !submitDisabled">
                            {{ $t('fileUpload.selectFile') }}
                        </el-button>
                    </div>
                </el-upload>
            </div>

            <div class="config-container">
                <TranslationConfig v-model="configuration"
                                   :pageNamePrefix="$t('fileUpload.configurationPrefix')"
                                   :expended="true"
                                   :readOptionsConfigEditable="true"
                                   :documentType="documentType"
                                   @configChanged="saveConfig" />
            </div>

            <FileUploadPrompt :submitDisabled="submitDisabled"
                              @submitted="submitFile" />

        </div>

    </div>
</template>

<script lang="ts">
    import { defineComponent } from 'vue';
    import { useI18n } from 'vue-i18n'

    import { TranslationConfiguration } from '@/models/TranslationConfiguration'
    import { DocumentType } from '@/models/DocumentType'

    import { navigationService } from '@/services/navigationService'
    import { homePageService } from '@/pages/Home/homePageService'
    import { userSettingsService } from '@/services/userSettingsService'
    import { translationConfigMapper } from '@/services/translationConfigMapper'
    import { notifyWarning } from '@/services/notificationService'
    import { getUserFromCache } from '@/stores/userStore'

    import TranslationConfig from '@/features/TranslationConfig/TranslationConfig.vue'
    import StorageStatusInformation from './StorageStatusInformation.vue'

    import FileUploadPrompt from './FileUploadPrompt.vue'

    interface FileTypeChip {
        ext: string;
        color: string;
        isNew: boolean;
    }

    export default defineComponent({
        components: {
            TranslationConfig,
            StorageStatusInformation,
            FileUploadPrompt,
        },
        setup() {
            const { t } = useI18n()
            return { t }
        },
        data() {
            return {
                loading: false,
                homePageService: new homePageService(),
                userSettingsService: new userSettingsService(),

                file: undefined as File | undefined,
                fileList: [] as any[],
                documentCount: 0,

                configuration: translationConfigMapper.GetDefaultConfiguration(),
            };
        },
        mounted() {
            this.init();
        },
        computed: {
            acceptedTypes(): Array<string> {
                let types = ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

                let user = getUserFromCache();
                if (user?.excelTranslationAccess) {
                    types.push('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
                }

                if (user?.powerpointTranslationAccess) {
                    types.push('application/vnd.openxmlformats-officedocument.presentationml.presentation')
                }

                if (user?.idmlTranslationAccess) {
                    types.push('application/vnd.adobe.indesign-idml-package')
                    types.push('.idml')
                }

                return types
            },
            fileTypeChips(): Array<FileTypeChip> {
                let chips: Array<FileTypeChip> = [{ ext: '.docx', color: 'var(--word-file)', isNew: false }];

                let user = getUserFromCache();
                if (user?.excelTranslationAccess) {
                    chips.push({ ext: '.xlsx', color: 'var(--excel-file)', isNew: false })
                }

                if (user?.powerpointTranslationAccess) {
                    chips.push({ ext: '.pptx', color: 'var(--powerpoint-file)', isNew: false })
                }

                if (user?.idmlTranslationAccess) {
                    chips.push({ ext: '.idml', color: 'var(--indesign-file)', isNew: true })
                }

                return chips
            },

            tittle(): string {
                return this.t('fileUpload.tittle');
            },
            uploadDisabled(): boolean {
                return this.homePageService.maxFileCountreached(this.documentCount);
            },
            submitDisabled(): boolean {
                return this.file == undefined;
            },
            documentType(): DocumentType {
                if (this.file != undefined) {
                    const name = this.file.name.toLowerCase();
                    if (name.endsWith('.xlsx')) {
                        return DocumentType.Xlsx;
                    } else if (name.endsWith('.pptx')) {
                        return DocumentType.Pptx;
                    } else if (name.endsWith('.idml')) {
                        return DocumentType.Idml;
                    }
                }

                return DocumentType.Docx;
            },
            defaultConfig(): TranslationConfiguration {
                return translationConfigMapper.GetDefaultConfiguration()
            },
        },
        methods: {
            async init() {
                this.loading = true;
                this.documentCount = await this.homePageService.getDocumentsCount();
                await this.setUserConfig();

                this.loading = false;
            },
            fileIsValid(newFile: File) {
                if (this.acceptedTypes.includes(newFile.type)) {
                    return true;
                } 
                
                if (newFile.name.toLowerCase().endsWith('.idml')) {
                    const user = getUserFromCache();
                    return user?.idmlTranslationAccess ?? false;
                }
                
                return false;
            },
            clearSelectedFile() {
                this.file = undefined;
                this.fileList = [];
            },
            handleFileChange(file: any) {
                const rawFile = file.raw as File;

                if (!rawFile) return;

                if (this.fileIsValid(rawFile)) {
                    this.file = rawFile;
                    this.fileList = [file];
                } else {
                    this.clearSelectedFile();
                    notifyWarning(this.t('fileUpload.notSupported'), this.t('fileUpload.selectCorrectFileType'));
                }
            },
            handleFileRemove() {
                this.clearSelectedFile();
            },
            async submitFile() {
                try {
                    if (!this.file) return;

                    this.loading = true;
                    var uploadedDocumentId = await this.homePageService.uploadDocument(this.file, this.configuration);
                    this.loading = false;

                    if (uploadedDocumentId) {
                        if (uploadedDocumentId == -1) {
                            this.$emit('upload-error');
                        } else {
                            navigationService.goDocumentPage(uploadedDocumentId);
                        }
                    } else {
                        // Todo show message something went wrong with upload
                    }
                } catch (e) {
                    console.error(e);
                    this.loading = false;
                }
            },
            async setUserConfig() {
                let userConfiguration = await this.userSettingsService.getUserTranslationConfig();

                if (userConfiguration != null) {
                    this.configuration = translationConfigMapper.MapConfigurations(this.configuration, userConfiguration)
                }
            },
            async saveConfig() {
                await this.userSettingsService.saveTranslationConfig(this.configuration);
            },
        }
    })
</script>

<style lang="scss" scoped>
    .tittle {
        display: flex;
        justify-content: space-around;
        margin: 30px 30px 16px;
        font-size: 26px;
    }

    .file-type-chips {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 18px;
        margin-bottom: 10px;
    }

    .file-type-chip {
        font-size: 15px;
        font-weight: 600;
        color: #ffffff;
        border: none;
    }

    .file-loader {
        margin: 60px;
        display: flex;
        justify-content: center;
        color: var(--text-color);
    }

    .config-container {
        margin-top: 20px;
    }

    .button-container{
        margin-bottom: 20px;
    }

    :deep(.el-upload-list__item-file-name) {
        font-size: 18px;
        color: var(--text-color);
    }

</style>